import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslate } from '@prenuvo/halo';

import { api } from '@/api';
import { useAddendum } from '@/core/addendum';
import { queryClient } from '@/core/react-query';
import { useStudyGuard } from '@/shared/guard';
import { useToastHandler } from '@/shared/hook';
import { redirectToRadDashboard } from '@/shared/util';
import { StudyMode, useStudyModeStore } from '@/store';

interface UseStudyModeDialog {
  canEnterAddendum: boolean;
  hasActiveSession: boolean;
  isCurrentUserSession: boolean;
  isEnteringAddendum: boolean;
  isLoading: boolean;
  isOpen: boolean;
  onEnterMode: (mode: StudyMode) => void;
  onExit: () => void;
}

export function useStudyModeDialog(): UseStudyModeDialog {
  const { report, studyId } = useStudyGuard();
  const resetStudyMode = useStudyModeStore((state) => state.resetStudyMode);
  const setStudyMode = useStudyModeStore((state) => state.setStudyMode);
  const setStudyModeResolving = useStudyModeStore((state) => state.setStudyModeResolving);
  const studyMode = useStudyModeStore((state) => state.studyMode);

  const {
    activeSession,
    canEnterAddendum,
    isAddendumEnabled,
    isCurrentUserSession,
    isSessionLoading,
  } = useAddendum();

  const translate = useTranslate();
  const toastRef = useRef<HTMLSpanElement>(null);
  const postAddendumSessionMutation =
    api.fct.addendumSession.mutation.usePostAddendumSession(queryClient);

  const showEnterAddendumErrorToast = useToastHandler({
    description: 'FCT.ADDENDUM.STUDY_MODE_DIALOG.ERROR.DESCRIPTION',
    title: translate('FCT.ADDENDUM.STUDY_MODE_DIALOG.ERROR.TITLE'),
    toastRef,
  });

  const shouldFetchSession = isAddendumEnabled && Boolean(report?.isReleased);

  const hasActiveSession = activeSession?.status === 'active';
  const shouldAutoEnterAddendum = hasActiveSession && canEnterAddendum;

  const isOpen = Boolean(
    shouldFetchSession && studyMode === null && !isSessionLoading && !shouldAutoEnterAddendum,
  );

  const enterAddendumMode = useCallback(() => {
    setStudyMode('addendum');
    queryClient.invalidateQueries({ queryKey: [api.fct.report.KEY.GET_REPORT_STUDY, studyId] });
  }, [setStudyMode, studyId]);

  const handleEnterMode = useCallback(
    (mode: StudyMode) => {
      if (mode === 'read-only') {
        setStudyMode('read-only');

        return;
      }

      postAddendumSessionMutation.mutate(
        { payload: { studyId } },
        {
          onError: () => showEnterAddendumErrorToast(),
          onSuccess: () => enterAddendumMode(),
        },
      );
    },
    [
      enterAddendumMode,
      postAddendumSessionMutation,
      setStudyMode,
      showEnterAddendumErrorToast,
      studyId,
    ],
  );

  const handleExit = useCallback(() => {
    redirectToRadDashboard();
  }, []);

  useEffect(() => {
    resetStudyMode();
  }, [studyId, resetStudyMode]);

  useEffect(() => {
    if (shouldFetchSession && shouldAutoEnterAddendum && studyMode === null) {
      enterAddendumMode();
    } else {
      setStudyModeResolving(Boolean(shouldFetchSession && studyMode === null));
    }
  }, [
    enterAddendumMode,
    setStudyModeResolving,
    shouldAutoEnterAddendum,
    shouldFetchSession,
    studyMode,
  ]);

  return useMemo(
    () => ({
      canEnterAddendum,
      hasActiveSession,
      isCurrentUserSession,
      isEnteringAddendum: postAddendumSessionMutation.isPending,
      isLoading: isSessionLoading,
      isOpen,
      onEnterMode: handleEnterMode,
      onExit: handleExit,
    }),
    [
      canEnterAddendum,
      handleEnterMode,
      handleExit,
      hasActiveSession,
      isCurrentUserSession,
      isOpen,
      isSessionLoading,
      postAddendumSessionMutation.isPending,
    ],
  );
}
